import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { propTypes } from '../../../util/types';
import { containsContactInfo } from '../../../util/contactInfoDetector';

import {
  ErrorMessage,
  FieldTextInput,
  Form,
  Heading,
  IconInquiry,
  PrimaryButton,
} from '../../../components';

import css from './InquiryForm.module.css';

/**
 * The InquiryForm component.
 * NOTE: this InquiryForm is only for booking & purchase processes
 * The default-inquiry process is handled differently
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.submitButtonWrapperClassName] - Custom class to be passed for the submit button wrapper
 * @param {boolean} [props.inProgress] - Whether the inquiry is in progress
 * @param {string} props.listingTitle - The listing title
 * @param {string} props.authorDisplayName - The author display name
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {boolean} [props.disableContactInfoBlocking] - When true, skip the contact-info warning (e.g. service listings)
 * @returns {JSX.Element} inquiry form component
 */
const InquiryForm = props => {
  const [showContactWarning, setShowContactWarning] = useState(false);

  return (
    <FinalForm
      {...props}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          submitButtonWrapperClassName,
          formId,
          handleSubmit,
          values,
          inProgress = false,
          listingTitle,
          authorDisplayName,
          sendInquiryError,
          disableContactInfoBlocking,
        } = fieldRenderProps;

        const intl = useIntl();
        const messageLabel = intl.formatMessage(
          { id: 'InquiryForm.messageLabel' },
          { authorDisplayName }
        );
        const messagePlaceholder = intl.formatMessage(
          { id: 'InquiryForm.messagePlaceholder' },
          { authorDisplayName }
        );
        const messageRequiredMessage = intl.formatMessage({
          id: 'InquiryForm.messageRequired',
        });
        const messageRequired = validators.requiredAndNonEmptyString(messageRequiredMessage);

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = inProgress;
        const submitDisabled = submitInProgress;

        const handleSubmitWithCheck = e => {
          e.preventDefault();
          const message = values?.message ? values.message.trim() : '';
          if (!disableContactInfoBlocking && containsContactInfo(message)) {
            setShowContactWarning(true);
            return;
          }
          handleSubmit(e);
        };

        return (
          <>
            <Form
              className={classes}
              onSubmit={handleSubmitWithCheck}
              enforcePagePreloadFor="OrderDetailsPage"
            >
              <IconInquiry className={css.icon} />
              <Heading as="h2" rootClassName={css.heading}>
                <FormattedMessage id="InquiryForm.heading" values={{ listingTitle }} />
              </Heading>
              <FieldTextInput
                className={css.field}
                type="textarea"
                name="message"
                id={formId ? `${formId}.message` : 'message'}
                label={messageLabel}
                placeholder={messagePlaceholder}
                validate={messageRequired}
              />
              <div className={submitButtonWrapperClassName}>
                <ErrorMessage error={sendInquiryError} />
                <PrimaryButton
                  type="submit"
                  inProgress={submitInProgress}
                  disabled={submitDisabled}
                >
                  <FormattedMessage id="InquiryForm.submitButtonText" />
                </PrimaryButton>
              </div>
            </Form>

            {showContactWarning ? (
              <div className={css.contactWarningOverlay} role="dialog" aria-modal="true">
                <div className={css.contactWarningDialog}>
                  <div className={css.contactWarningIconRow}>
                    <span className={css.contactWarningIcon}>🔒</span>
                  </div>
                  <h3 className={css.contactWarningTitle}>
                    <FormattedMessage id="TransactionPanel.contactWarning.title" />
                  </h3>
                  <p className={css.contactWarningBody}>
                    <FormattedMessage id="TransactionPanel.contactWarning.body" />
                  </p>
                  <p className={css.contactWarningRestriction}>
                    <FormattedMessage id="TransactionPanel.contactWarning.restriction" />
                  </p>
                  <button
                    className={css.contactWarningButton}
                    onClick={() => setShowContactWarning(false)}
                    type="button"
                  >
                    <FormattedMessage id="TransactionPanel.contactWarning.dismiss" />
                  </button>
                </div>
              </div>
            ) : null}
          </>
        );
      }}
    />
  );
};

export default InquiryForm;
